import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { Input, Listbox, ListboxItem } from "@nextui-org/react";
import { SearchIcon } from "../../icons/SearchIcon";
import Header from "../../reusable/Header";
import PoweredBy from "../../reusable/PoweredBy";
import { useDispatch, useSelector } from "react-redux";
import { getGovernmentDatabasesByFlow } from "../../../store/verification.slice";

function GovernmentDatabaseCountrySelect({ onSelect }) {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const dispatch = useDispatch();
    const { governmentDatabases, credentials } = useSelector((store) => store.verification);
    // Local to this screen rather than the global redux `loading` flag: that
    // flag also drives Home.jsx's top-level view switch, which unmounts
    // <Verification> (and this component with it) whenever it's true -- see
    // getGovernmentDatabasesByFlow in verification.slice.jsx.
    const loading = governmentDatabases == null;

    const search = window.location.search;
    const params = new URLSearchParams(search);
    const clientId = params.get('clientId');
    const flowId = params.get('flowId');

    useEffect(() => {
        if (flowId && clientId && credentials?.accessToken && !governmentDatabases) {
            dispatch(getGovernmentDatabasesByFlow(flowId, clientId, credentials.accessToken));
        }
    }, [flowId, clientId, credentials, dispatch, governmentDatabases]);

    const filteredCountries = governmentDatabases?.filter(country =>
        country.countryName.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    const handleSelect = (selected) => {
        if (selected) {
            const selectedKey = selected.values().next().value;
            const country = governmentDatabases.find(c => c.countryCode === selectedKey);
            if (country) {
                onSelect(country);
            }
        }
    };

    return (
        <div className="flex h-full flex-col">
            {/* Fixed Header */}
            <div className="top-0 left-0">
                <Header />
                <div className="p-4 flex flex-col gap-4">
                    <div className="font-normal text-xl text-center" style={{color: "var(--urtentic-sdk-title, #000000)"}}>{t("governmentDatabases.countrySelect.heading")}</div>
                    <Input
                        type="text"
                        variant="bordered"
                        placeholder={t("governmentDatabases.countrySelect.searchPlaceholder")}
                        radius="sm"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        startContent={
                            <SearchIcon
                                className="text-2xl pointer-events-none flex-shrink-0"
                                style={{ color: "var(--urtentic-sdk-primary, #255CFF)" }}
                            />
                        }
                    />
                </div>
            </div>

            {/* Scrollable Listbox Section */}
            <div className="flex flex-col p-4 justify-between flex-grow overflow-auto">
                <div className="px-1 py-2">
                    {loading ? (
                        <div className="desc text-center">{t("governmentDatabases.countrySelect.loading")}</div>
                    ) : filteredCountries.length === 0 ? (
                        <div className="desc text-center">{t("governmentDatabases.countrySelect.noCountries")}</div>
                    ) : (
                        <Listbox
                            aria-label="Select country"
                            variant="flat"
                            disallowEmptySelection
                            selectionMode="single"
                            onSelectionChange={handleSelect}
                        >
                            {filteredCountries.map((country) => (
                                <ListboxItem
                                    key={country.countryCode}
                                    showDivider
                                    startContent={
                                        <img
                                            src={country.countryFlag}
                                            alt={country.countryName}
                                            className="w-6 h-4 object-cover rounded-sm"
                                        />
                                    }
                                >
                                    <span style={{color: "var(--urtentic-sdk-body, #737373)"}}>{country.countryName}</span>
                                </ListboxItem>
                            ))}
                        </Listbox>
                    )}
                </div>
            </div>

            {/* Fixed PoweredBy Section */}
            <div className="flex justify-center p-2">
                <PoweredBy />
            </div>
        </div>
    );
}

export default GovernmentDatabaseCountrySelect;
